import * as React from "react";
import { StatusType } from "../../types/common/StatusTypes";

// Single, unified interface definition to fix the "merged declaration" error
export interface StatusBadgeProps {
  /**
   * The status type (determines color when variant is not provided)
   */
  status: StatusType;

  /**
   * The content to display inside the badge
   */
  children: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Test ID for automated testing
   */
  testId?: string;

  /**
   * Optional size variant
   */
  size?: "sm" | "md" | "lg";

  /**
   * Badge color scheme override. When provided and matches a known type,
   * overrides the color derived from `status`. This allows callers to
   * decouple the semantic status from the visual presentation.
   *
   * @example
   * ```tsx
   * // Color driven by status (default behavior)
   * <StatusBadge status="success">OK</StatusBadge>
   *
   * // Color overridden by variant
   * <StatusBadge status="info" variant="warning">Needs attention</StatusBadge>
   * ```
   */
  variant?: "error" | "warning" | "info" | "success" | "neutral" | string;
}

/** Known variant values that map to specific color schemes */
const KNOWN_VARIANTS = new Set<string>([
  "error",
  "warning",
  "info",
  "success",
  "neutral",
  "purple",
]);

/**
 * Displays a status badge with appropriate colors
 *
 * ## UX Perspective
 *
 * Provides consistent visual indicators of status throughout the
 * application, using color psychology to communicate severity and
 * importance at a glance. 🎨
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  children,
  className = "",
  testId,
  size = "md", // Default to medium size
  variant,
}) => {
  // Use variant as color key when explicitly provided and recognized,
  // otherwise fall back to the status prop.
  const colorKey: string =
    variant && KNOWN_VARIANTS.has(variant) ? variant : status;

  // Determine color classes based on resolved color key
  const getStatusClasses = (): string => {
    switch (colorKey) {
      case "success":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "info":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "warning":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "error":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "neutral":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
      case "purple":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  // Determine size classes
  const getSizeClasses = (): string => {
    switch (size) {
      case "sm":
        return "px-1.5 py-0.5 text-xs";
      case "lg":
        return "px-3 py-1.5 text-sm";
      default: // md
        return "px-2 py-1 text-xs";
    }
  };

  return (
    <span
      className={`font-medium rounded-md ${getStatusClasses()} ${getSizeClasses()} ${className}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
};

export default StatusBadge;
// Use imported types rather than redefining them
export type { StatusType };
